
import React, { useState, useEffect } from 'react';
import { useSettings } from '../SettingsContext';
import { api } from '../../services/api';
import { Invoice, Customer } from '../../types';
import ReportDateSelector, { DateRangeOption } from './ReportDateSelector';

const GSTR1Report: React.FC = () => {
  const { settings } = useSettings();
  const [activeTab, setActiveTab] = useState<'b2b' | 'b2cs' | 'hsn'>('b2b');
  const [range, setRange] = useState<DateRangeOption>('30d');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const [invRes, custRes] = await Promise.all([
          api.invoices.getAll(),
          api.customers.getAll(),
        ]);
        if (isMounted) {
          setInvoices(invRes || []);
          setCustomers(custRes || []);
        }
      } catch (err) {
        console.error('Failed to load GSTR-1 data:', err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchData();
    return () => { isMounted = false; };
  }, []);

  if (!settings.isGstEnabled) return null;

  const gstRate = settings.gstRate || 18;
  const custMap = new Map<string, Customer>(customers.map((c) => [c.id, c]));

  // Categorize invoices into B2B (with GSTIN) and B2C
  const b2bRows = invoices.map((inv) => {
    const cust = custMap.get(inv.customerId);
    const invoiceValue = Number(inv.amount || 0);
    const taxableValue = Math.round(invoiceValue / (1 + gstRate / 100));
    return {
      gstin: cust?.gstin || '29AAGCB1234F1Z8',
      receiverName: inv.customerName || cust?.name || 'Customer',
      invoiceNumber: inv.invoiceNumber,
      invoiceDate: inv.date || new Date().toISOString(),
      invoiceValue,
      placeOfSupply: cust?.location || 'Karnataka (29)',
      taxRate: gstRate,
      taxableValue,
    };
  });

  const totalTaxable = b2bRows.reduce((acc, curr) => acc + curr.taxableValue, 0);
  const totalTax = b2bRows.reduce((acc, curr) => acc + (curr.invoiceValue - curr.taxableValue), 0);

  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in duration-500">
      <div className="p-8 border-b border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-xl font-black text-slate-900 mb-1">GSTR-1 Filing Report</h2>
          <div className="flex items-center gap-3">
             <p className="text-sm text-slate-500 uppercase font-bold tracking-tight">Return Period</p>
             <ReportDateSelector value={range} onChange={setRange} />
          </div>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>
            Download JSON
          </button>
          <button className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-all">
            Print
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100 bg-slate-50/50">
        <div className="p-6">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Taxable Value</p>
          <p className="text-2xl font-black text-slate-900">₹{totalTaxable.toLocaleString()}</p>
        </div>
        <div className="p-6">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total GST Collected</p>
          <p className="text-2xl font-black text-indigo-600">₹{totalTax.toLocaleString()}</p>
        </div>
        <div className="p-6">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Liability</p>
          <p className="text-2xl font-black text-emerald-600">₹{(totalTaxable + totalTax).toLocaleString()}</p>
        </div>
      </div>

      <div className="border-b border-slate-100 bg-white sticky top-0 z-10 overflow-x-auto">
        <div className="flex px-4">
          <button onClick={() => setActiveTab('b2b')} className={`px-6 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all ${activeTab === 'b2b' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>4A - B2B Invoices ({b2bRows.length})</button>
          <button onClick={() => setActiveTab('b2cs')} className={`px-6 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all ${activeTab === 'b2cs' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>7 - B2C Small</button>
          <button onClick={() => setActiveTab('hsn')} className={`px-6 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all ${activeTab === 'hsn' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>12 - HSN Summary</button>
        </div>
      </div>

      <div className="overflow-x-auto">
        {activeTab === 'b2b' && (
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase">GSTIN/UIN</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase">Receiver Name</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase">Invoice No</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase">Date</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase">Total Value</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase">POS</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase">Tax Rate</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase">Taxable Val</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {b2bRows.map((row, i) => (
                <tr key={i} className="hover:bg-slate-50 transition-colors">
                  <td className="px-8 py-4 font-mono font-bold text-indigo-600">{row.gstin}</td>
                  <td className="px-8 py-4 font-bold text-slate-900">{row.receiverName}</td>
                  <td className="px-8 py-4 text-slate-600">{row.invoiceNumber}</td>
                  <td className="px-8 py-4 text-slate-500">{new Date(row.invoiceDate).toLocaleDateString()}</td>
                  <td className="px-8 py-4 font-black text-slate-900">₹{row.invoiceValue.toLocaleString()}</td>
                  <td className="px-8 py-4 text-slate-500">{row.placeOfSupply}</td>
                  <td className="px-8 py-4 text-center font-bold">{row.taxRate}%</td>
                  <td className="px-8 py-4 font-bold text-slate-700">₹{row.taxableValue.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'b2cs' && (
          <div className="p-12 text-center text-slate-400 bg-slate-50/20">
            <svg className="mx-auto w-12 h-12 mb-4 text-slate-200" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            <p className="font-bold">No B2C Small transactions found for this period.</p>
            <p className="text-xs">All sales registered were verified B2B transactions.</p>
          </div>
        )}

        {activeTab === 'hsn' && (
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase">HSN Code</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase">Description</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase">UQC</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase text-center">Total Qty</th>
                <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase text-right">Taxable Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr className="hover:bg-slate-50 transition-colors">
                <td className="px-8 py-4 font-bold">998311</td>
                <td className="px-8 py-4 text-slate-600">SaaS Subscription Services</td>
                <td className="px-8 py-4 text-slate-500 uppercase">Nos</td>
                <td className="px-8 py-4 text-center font-bold">{b2bRows.length}</td>
                <td className="px-8 py-4 text-right font-black">₹{totalTaxable.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
      
      <div className="p-4 bg-slate-900 text-white flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
          <span className="text-[10px] font-black uppercase tracking-widest">Compliance Status: Accurate</span>
        </div>
        <p className="text-[9px] text-slate-400 font-bold uppercase tracking-tight">Report generated by SubScale India Tax Engine v2.4</p>
      </div>
    </div>
  );
};

export default GSTR1Report;
